import { useEffect, useMemo, useState, useRef } from "react";
import moment from "moment";
import { Student, Assignment } from "@/models";
import { GradeNumericToString, SkillType } from "@/enums";
import { useRosterInfo } from "@/hooks";
import { getTeacherAssignments } from "@/services/weeklyPlanner/query/getTeacherAssignments";
import { getCurriculum } from "@/services/weeklyPlanner/query/getCurriculum";
import { aggregateAssignmentsToLessons } from "@/utilities/aggregateAssignmentsToLessons";

interface UseTargetWeekCurriculumDataProps {
  dateFrom: Date;
  dateTo: Date;
}

export function useTargetWeekCurriculumData({ dateFrom, dateTo }: UseTargetWeekCurriculumDataProps) {
  const { students: studentsRoster, classroomId } = useRosterInfo();
  const [isLoading, setIsLoading] = useState(true);
  const [curriculumData, setCurriculumData] = useState<any>(null);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [error, setError] = useState<string | null>(null);

  const hasFetchedCurriculum = useRef(false);

  const students: Student[] = useMemo(() => {
    const filteredStudents = studentsRoster
      .filter((s: any) => s.classroom_id.find((id: string) => `${id}` === `${classroomId}`))
      .map((s: any) => ({
        ...s,
        firstName: s.first_name,
        lastName: s.last_name,
      }));

    return filteredStudents;
  }, [studentsRoster, classroomId]);
  // console.log("🚀 ~ students ~ students:", students)

  // Reset fetch flags when classroom changes
  useEffect(() => {
    hasFetchedCurriculum.current = false;
    setCurriculumData(null);
    setError(null);
  }, [classroomId]);

  // Fetch curriculum data for the target week
  useEffect(() => {
    if (!classroomId || hasFetchedCurriculum.current) return;

    const fetchTargetWeekCurriculum = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const assignments = await getTeacherAssignments(
          classroomId,
          moment(dateFrom).format("YYYY-MM-DD"),
          moment(dateTo).format("YYYY-MM-DD"),
          GradeNumericToString[studentsRoster[0]?.grade]
        );

        setAssignments(assignments);

        const lessonPlanIds = [
          ...new Set(
            assignments?.assignment
              ?.map((a: any) => a.lessonPlanId)
              .filter(Boolean)
          ),
        ];

        if (lessonPlanIds.length > 0) {
          const curriculumResponse = await getCurriculum(lessonPlanIds);
          if (curriculumResponse?.getCurriculumInfoForLessons) {
            const aggregatedCurriculum = aggregateAssignmentsToLessons({
              assignments: assignments.assignment,
              curriculumInfo: curriculumResponse.getCurriculumInfoForLessons,
              dateFrom: moment(dateFrom).format("YYYY-MM-DD"),
              dateTo: moment(dateTo).format("YYYY-MM-DD"),
              stories: {},
              studentCount: students.length,
            });

            setCurriculumData(aggregatedCurriculum);
          }
        } else {
          setCurriculumData(null);
        }
      } catch (error) {
        console.error("Error fetching target week curriculum:", error);
        setError(error instanceof Error ? error.message : "Unknown error occurred");
      } finally {
        hasFetchedCurriculum.current = true;
        setIsLoading(false);
      }
    };

    fetchTargetWeekCurriculum();
  }, [
    classroomId,
    studentsRoster,
    dateFrom,
    dateTo,
    students.length,
  ]);

  // Extract focus skills from curriculum data
  const focusSkills = useMemo(() => {
    const skills: any[] = [];

    if (curriculumData?.tracks) {
      
      curriculumData.tracks.forEach((track: any, trackIndex: number) => {
        
        track.units?.forEach((unit: any, unitIndex: number) => {
          
          unit.lessons?.forEach((lesson: any, lessonIndex: number) => {
            
            const lessonDate = moment(lesson.dateFrom);
            const isInDateRange = lessonDate.isBetween(moment(dateFrom), moment(dateTo), "day", "[]");
            if (isInDateRange) {
              const focusSkillsInLesson = lesson.skills.filter((skill: any) => skill.skillType === SkillType.FOCUS);
              
              focusSkillsInLesson.forEach((skill: any) => {
                const skillData = {
                  ...skill,
                  lessonName: lesson.lessonName || lesson.name,
                  lessonDate: lesson.dateFrom,
                  unitName: unit.name,
                  trackName: track.name,
                  curriculumName: lesson.curriculumName || curriculumData?.name,
                  curriculumId: lesson.curriculumId,
                  lessonPlanId: lesson.lessonPlanId,
                };
                skills.push(skillData);
              });
            }
          });
        });
      });
    }
    return skills;
  }, [curriculumData, dateFrom, dateTo]);

  return {
    curriculumData,
    assignments,
    focusSkills,
    isLoading,
    error,
    students,
  };
}

export default useTargetWeekCurriculumData; 